import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { getStudents } from "../../services/students";
import eCourseService from "../../services/elearning/eCourseService";
import api from "../../services/api";
import {
  User,
  GraduationCap,
  CheckCircle,
  XCircle,
  Search,
  UserPlus,
} from "lucide-react";

interface Student {
  _id: string;
  userId: string;
  admissionNumber: string;
  studentId: string;
  studentType?: "regular" | "ecourse";
  status?: string;
  userInfo: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface Course {
  _id: string;
  title: string;
  description: string;
  thumbnail?: string;
  pricing?: {
    type: "free" | "paid";
    amount?: number;
  };
  registration?: {
    maxStudents?: number;
  };
  stats?: {
    totalStudents?: number;
  };
  status: string;
}

const ManualEnrollment: React.FC = () => {
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [notes, setNotes] = useState("");
  const [studentSearch, setStudentSearch] = useState("");
  const [courseSearch, setCourseSearch] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const queryClient = useQueryClient();

  // Fetch students
  const {
    data: studentsData,
    isLoading: studentsLoading,
    error: studentsError,
  } = useQuery({
    queryKey: ["students", studentSearch],
    queryFn: () =>
      getStudents({
        search: studentSearch,
        limit: 20,
        includeEcourse: "true",
      }),
  });

  // Fetch published courses
  const {
    data: coursesData,
    isLoading: coursesLoading,
    error: coursesError,
  } = useQuery({
    queryKey: ["public-courses", courseSearch],
    queryFn: async () => {
      const response = await eCourseService.getPublicCourses({
        search: courseSearch,
        limit: 20,
      });
      return response;
    },
  });

  // Manual enrollment mutation
  const enrollMutation = useMutation({
    mutationFn: async ({
      courseId,
      studentId,
      notes,
    }: {
      courseId: string;
      studentId: string;
      notes: string;
    }) => {
      const response = await api.post(
        `/elearning/courses/${courseId}/enroll-student`,
        {
          studentId,
          notes,
        }
      );
      return response;
    },
    onSuccess: (data: any) => {
      toast.success("Student enrolled successfully!");
      queryClient.invalidateQueries({ queryKey: ["public-courses"] });
      queryClient.invalidateQueries({ queryKey: ["enrollments"] });

      // Reset form
      setSelectedStudent(null);
      setSelectedCourse(null);
      setNotes("");
      setShowConfirmDialog(false);
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.message || "Failed to enroll student";
      toast.error(message);
    },
  });

  const handleEnroll = () => {
    if (!selectedStudent || !selectedCourse) {
      toast.error("Please select both a student and a course");
      return;
    }
    setShowConfirmDialog(true);
  };

  const confirmEnrollment = () => {
    if (selectedStudent && selectedCourse) {
      console.log("Enrolling student:", selectedStudent);
      enrollMutation.mutate({
        courseId: selectedCourse._id,
        studentId: selectedStudent.userId,
        notes: notes || "Manually enrolled by admin",
      });
    }
  };

  const students = studentsData?.data || [];
  const courses = coursesData?.data?.courses || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-3 sm:p-4 shadow-lg w-fit">
              <UserPlus className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">
                Manual Student Enrollment
              </h1>
              <p className="text-slate-600 font-semibold mt-1 text-sm sm:text-base">
                Enroll students into e-learning courses manually for cash
                payments or administrative purposes
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Student Selection */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-xl p-2 shadow-lg">
                <User className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-slate-900">
                Select Student
              </h2>
            </div>

            {/* Student Search */}
            <div className="mb-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by name, email, or ID..."
                  value={studentSearch}
                  onChange={(e) => setStudentSearch(e.target.value)}
                  className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-700 text-sm sm:text-base placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-300"
                />
              </div>
            </div>

            {/* Selected Student Display */}
            {selectedStudent && (
              <div className="mb-4 p-3 sm:p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl shadow-md">
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-900 text-sm sm:text-base truncate">
                      {selectedStudent.userInfo.firstName}{" "}
                      {selectedStudent.userInfo.lastName}
                    </p>
                    <p className="text-xs sm:text-sm text-slate-600 font-semibold truncate">
                      {selectedStudent.admissionNumber}
                    </p>
                    <span className="inline-flex mt-2 px-2 py-1 text-xs font-bold rounded-lg shadow-md bg-gradient-to-r from-blue-300 to-indigo-400 text-white">
                      {selectedStudent.studentType === "ecourse"
                        ? "E-Course Student"
                        : "Regular Student"}
                    </span>
                  </div>
                  <button
                    onClick={() => setSelectedStudent(null)}
                    className="p-1.5 sm:p-2 bg-white border-2 border-slate-200 rounded-lg hover:shadow-lg hover:scale-110 transition-all duration-300 flex-shrink-0"
                  >
                    <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
                  </button>
                </div>
              </div>
            )}

            {/* Student List */}
            <div className="max-h-64 sm:max-h-96 overflow-y-auto space-y-2">
              {studentsLoading && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-4 border-slate-200 border-t-blue-500 mx-auto mb-4"></div>
                  <span className="text-sm sm:text-base font-bold text-slate-600">
                    Loading students...
                  </span>
                </div>
              )}
              {studentsError && (
                <div className="text-center py-8">
                  <div className="bg-gradient-to-br from-red-300 to-rose-400 rounded-2xl p-6 sm:p-8 shadow-lg inline-block mb-4">
                    <XCircle className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                  </div>
                  <p className="text-sm sm:text-base font-bold text-red-600">
                    Error loading students
                  </p>
                </div>
              )}
              {!studentsLoading && students.length === 0 && (
                <div className="text-center py-8">
                  <div className="bg-gradient-to-br from-slate-300 to-gray-400 rounded-2xl p-6 sm:p-8 shadow-lg inline-block mb-4">
                    <User className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                  </div>
                  <p className="text-sm sm:text-base font-bold text-slate-600">
                    No students found
                  </p>
                </div>
              )}
              {students.map((student: any) => (
                <button
                  key={student._id}
                  onClick={() => setSelectedStudent(student)}
                  disabled={selectedStudent?._id === student._id}
                  className={`w-full text-left p-3 rounded-xl border-2 transition-all duration-300 ${
                    selectedStudent?._id === student._id
                      ? "border-blue-500 bg-gradient-to-br from-blue-100 to-indigo-100 shadow-lg scale-105"
                      : "border-blue-200 bg-gradient-to-br from-blue-50/50 to-indigo-50/50 hover:border-blue-400 hover:from-blue-100/80 hover:to-indigo-100/80 hover:shadow-lg hover:scale-102"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-900 text-sm sm:text-base truncate">
                        {student.userInfo?.firstName}{" "}
                        {student.userInfo?.lastName}
                      </p>
                      <p className="text-xs sm:text-sm text-slate-600 font-semibold truncate">
                        {student.admissionNumber}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span
                        className={`px-2 py-1 text-xs font-bold rounded-lg shadow-md ${
                          student.studentType === "ecourse"
                            ? "bg-gradient-to-r from-purple-300 to-indigo-400 text-white"
                            : "bg-gradient-to-r from-emerald-300 to-green-400 text-white"
                        }`}
                      >
                        {student.studentType === "ecourse"
                          ? "E-Course"
                          : "Regular"}
                      </span>
                      {selectedStudent?._id === student._id && (
                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Course Selection */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="bg-gradient-to-br from-emerald-300 to-green-400 rounded-xl p-2 shadow-lg">
                <GraduationCap className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-slate-900">
                Select Course
              </h2>
            </div>

            {/* Course Search */}
            <div className="mb-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search courses..."
                  value={courseSearch}
                  onChange={(e) => setCourseSearch(e.target.value)}
                  className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-700 text-sm sm:text-base placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-300"
                />
              </div>
            </div>

            {/* Selected Course Display */}
            {selectedCourse && (
              <div className="mb-4 p-3 sm:p-4 bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-300 rounded-xl shadow-md">
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-900 text-sm sm:text-base">
                      {selectedCourse.title}
                    </p>
                    <p className="text-xs sm:text-sm text-slate-600 font-semibold mt-1 line-clamp-2">
                      {selectedCourse.description?.substring(0, 100)}...
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <span
                        className={`px-2 py-1 text-xs font-bold rounded-lg shadow-md ${
                          selectedCourse.pricing?.type === "paid"
                            ? "bg-gradient-to-r from-amber-300 to-orange-400 text-white"
                            : "bg-gradient-to-r from-emerald-300 to-green-400 text-white"
                        }`}
                      >
                        {selectedCourse.pricing?.type === "paid"
                          ? `KES ${selectedCourse.pricing.amount}`
                          : "Free"}
                      </span>
                      {selectedCourse.registration?.maxStudents && (
                        <span className="text-xs font-bold text-slate-600">
                          {selectedCourse.stats?.totalStudents || 0}/
                          {selectedCourse.registration.maxStudents} enrolled
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedCourse(null)}
                    className="p-1.5 sm:p-2 bg-white border-2 border-slate-200 rounded-lg hover:shadow-lg hover:scale-110 transition-all duration-300 flex-shrink-0"
                  >
                    <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
                  </button>
                </div>
              </div>
            )}

            {/* Course List */}
            <div className="max-h-64 sm:max-h-96 overflow-y-auto space-y-2">
              {coursesLoading && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-4 border-slate-200 border-t-blue-500 mx-auto mb-4"></div>
                  <span className="text-sm sm:text-base font-bold text-slate-600">
                    Loading courses...
                  </span>
                </div>
              )}
              {coursesError && (
                <div className="text-center py-8">
                  <div className="bg-gradient-to-br from-red-300 to-rose-400 rounded-2xl p-6 sm:p-8 shadow-lg inline-block mb-4">
                    <XCircle className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                  </div>
                  <p className="text-sm sm:text-base font-bold text-red-600">
                    Error loading courses
                  </p>
                </div>
              )}
              {!coursesLoading && courses.length === 0 && (
                <div className="text-center py-8">
                  <div className="bg-gradient-to-br from-slate-300 to-gray-400 rounded-2xl p-6 sm:p-8 shadow-lg inline-block mb-4">
                    <GraduationCap className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                  </div>
                  <p className="text-sm sm:text-base font-bold text-slate-600">
                    No courses found
                  </p>
                </div>
              )}
              {courses.map((course: Course) => (
                <button
                  key={course._id}
                  onClick={() => setSelectedCourse(course)}
                  disabled={
                    selectedCourse?._id === course._id ||
                    course.status !== "published"
                  }
                  className={`w-full text-left p-3 rounded-xl border-2 transition-all duration-300 ${
                    selectedCourse?._id === course._id
                      ? "border-emerald-500 bg-gradient-to-br from-emerald-100 to-green-100 shadow-lg scale-105"
                      : course.status !== "published"
                      ? "border-slate-300 bg-gradient-to-br from-slate-100 to-gray-100 opacity-50 cursor-not-allowed"
                      : "border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-green-50/50 hover:border-emerald-400 hover:from-emerald-100/80 hover:to-green-100/80 hover:shadow-lg hover:scale-102"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-900 text-sm sm:text-base">
                        {course.title}
                      </p>
                      <p className="text-xs sm:text-sm text-slate-600 font-semibold line-clamp-2 mt-1">
                        {course.description}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <span
                          className={`px-2 py-1 text-xs font-bold rounded-lg shadow-md ${
                            course.pricing?.type === "paid"
                              ? "bg-gradient-to-r from-amber-300 to-orange-400 text-white"
                              : "bg-gradient-to-r from-emerald-300 to-green-400 text-white"
                          }`}
                        >
                          {course.pricing?.type === "paid"
                            ? `KES ${course.pricing.amount}`
                            : "Free"}
                        </span>
                        {course.registration?.maxStudents && (
                          <span className="text-xs font-bold text-slate-600">
                            {course.stats?.totalStudents || 0}/
                            {course.registration.maxStudents}
                          </span>
                        )}
                      </div>
                    </div>
                    {selectedCourse?._id === course._id && (
                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 flex-shrink-0" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Notes and Submit Section */}
        <div className="mt-4 sm:mt-6 bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-3 sm:mb-4">
            Enrollment Notes *
          </h3>
          <textarea
            value={notes}
            required
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes about this enrollment (e.g., 'Cash payment received - KES 5000 on 2024-01-06')"
            rows={3}
            className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-700 text-sm sm:text-base placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none"
          />

          <div className="mt-4 flex flex-col sm:flex-row justify-end gap-3">
            <button
              onClick={() => {
                setSelectedStudent(null);
                setSelectedCourse(null);
                setNotes("");
              }}
              className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-white border-2 border-slate-200 rounded-xl font-bold text-slate-700 text-sm sm:text-base hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              Clear Selection
            </button>
            <button
              onClick={handleEnroll}
              disabled={
                !selectedStudent ||
                !selectedCourse ||
                !notes ||
                enrollMutation.isPending
              }
              className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-bold text-sm sm:text-base hover:shadow-lg hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all duration-300"
            >
              {enrollMutation.isPending ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Enrolling...
                </span>
              ) : (
                "Enroll Student"
              )}
            </button>
          </div>
        </div>

        {/* Confirmation Dialog */}
        {showConfirmDialog && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-2xl max-w-md w-full">
              {/* Header */}
              <div className="p-4 sm:p-6 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-xl p-2 sm:p-3 shadow-lg">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                    Confirm Enrollment
                  </h3>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                <div>
                  <p className="text-xs sm:text-sm font-bold text-slate-600 mb-1">
                    Student:
                  </p>
                  <p className="font-bold text-slate-900 text-sm sm:text-base">
                    {selectedStudent?.userInfo.firstName}{" "}
                    {selectedStudent?.userInfo.lastName}
                  </p>
                  <p className="text-xs sm:text-sm text-slate-600 font-semibold">
                    {selectedStudent?.admissionNumber}
                  </p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm font-bold text-slate-600 mb-1">
                    Course:
                  </p>
                  <p className="font-bold text-slate-900 text-sm sm:text-base">
                    {selectedCourse?.title}
                  </p>
                  {selectedCourse?.pricing?.type === "paid" && (
                    <p className="text-xs sm:text-sm font-bold text-amber-600 mt-1">
                      Course Fee: KES {selectedCourse.pricing.amount} (bypassed)
                    </p>
                  )}
                </div>
                {notes && (
                  <div>
                    <p className="text-xs sm:text-sm font-bold text-slate-600 mb-1">
                      Notes:
                    </p>
                    <p className="text-xs sm:text-sm font-semibold text-slate-900 bg-gradient-to-r from-slate-50 to-blue-50 p-3 rounded-xl border border-slate-200">
                      {notes}
                    </p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 sm:p-6 border-t border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setShowConfirmDialog(false)}
                  className="w-full sm:flex-1 px-4 py-2 sm:py-3 bg-white border-2 border-slate-200 rounded-xl font-bold text-slate-700 text-sm sm:text-base hover:shadow-lg hover:scale-105 transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmEnrollment}
                  disabled={enrollMutation.isPending}
                  className="w-full sm:flex-1 px-4 py-2 sm:py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-bold text-sm sm:text-base hover:shadow-lg hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all duration-300"
                >
                  {enrollMutation.isPending ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Enrolling...
                    </span>
                  ) : (
                    "Confirm"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManualEnrollment;
